import { Link } from "react-router-dom";
import { useMemo, useState } from "react";

import { FlightTable } from "@/components/FlightTable";
import type { DashboardFlightRow } from "@/types/api";
import type { WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

type Column =
  | "session"
  | "date"
  | "duration"
  | "distance"
  | "avg_speed"
  | "smoothness"
  | "status";

function RecentFlightsSmall({ rows }: { rows: DashboardFlightRow[] }) {
  const top = rows.slice(0, 3);
  return (
    <div className="recent-flights recent-flights--s">
      <p className="eyebrow">Recent flights</p>
      <ul className="recent-flights__mini-list">
        {top.map((row) => (
          <li key={row.id}>
            <Link to={`/flights/${row.id}`} className="recent-flights__mini-row">
              <div className="recent-flights__mini-primary">
                <span className="recent-flights__mini-title">
                  {row.session_name || `Flight ${row.id}`}
                </span>
                <span className="recent-flights__mini-date">{row.date_display}</span>
              </div>
              <span className="recent-flights__mini-duration">
                {row.duration_display}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <Link to="/flights" className="text-link">
        All →
      </Link>
    </div>
  );
}

function RecentFlightsMedium({ rows }: { rows: DashboardFlightRow[] }) {
  const columns: Column[] = ["session", "date", "duration", "distance", "status"];
  return (
    <div className="recent-flights recent-flights--m">
      <FlightTable rows={rows.slice(0, 5)} columns={columns} />
      <div className="recent-flights__footer">
        <Link to="/flights" className="text-link">
          All flights →
        </Link>
      </div>
    </div>
  );
}

function RecentFlightsLarge({ rows }: { rows: DashboardFlightRow[] }) {
  const [filter, setFilter] = useState<"all" | "ready" | "failed">("all");
  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const columns: Column[] = [
    "session",
    "date",
    "duration",
    "distance",
    "avg_speed",
    "smoothness",
    "status",
  ];

  return (
    <div className="recent-flights recent-flights--l">
      <div className="recent-flights__head">
        <p className="eyebrow">Recent flights · {rows.length} latest</p>
        <div className="recent-flights__filters">
          {(["all", "ready", "failed"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`pill-button ${filter === f ? "pill-button--active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? "All" : f === "ready" ? "Ready" : "Failed"}
            </button>
          ))}
        </div>
      </div>
      <FlightTable rows={filtered.slice(0, 10)} columns={columns} />
      <div className="recent-flights__footer">
        <Link to="/flights" className="text-link">
          All flights →
        </Link>
      </div>
    </div>
  );
}

export function RecentFlightsWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <p className="widget-empty">Loading…</p>;
  const rows = data?.recent_flights ?? [];
  if (!rows.length) return <p className="widget-empty">No flights yet.</p>;

  if (sizeVariant === "s") return <RecentFlightsSmall rows={rows} />;
  if (sizeVariant === "l") return <RecentFlightsLarge rows={rows} />;
  return <RecentFlightsMedium rows={rows} />;
}
